/*
 * @Author: Claude
 * @Date: 2025-11-08
 * @Description: 文件校验 Hook
 */
import { useCallback } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import { t } from '@/utils/helper/intl';

import { FileUploadProps } from '../../types';
import { ExtendedUploadFile } from '../types';
import { FileUploadRule, ValidationResult } from '../types/validator';
import { isFileTypeMatch } from '../utils/fileTypeMatch';
import { validateFileSize } from '../utils/validateFileSize';
import {
  validateCustomRule,
  validateImageAspectRatio,
  validateImageResolution,
  validateMediaDuration,
} from '../validators';

interface UseFileValidatorOptions {
  rules?: FileUploadRule[];
  fileList?: ExtendedUploadFile[];
  accept?: string; // 文件类型限制
}

/**
 * 文件校验 Hook
 * 支持文件大小、媒体时长、图片分辨率校验和文件类型过滤
 */
export const useFileValidator = (options: UseFileValidatorOptions = {}) => {
  const { rules = [], fileList = [], accept } = options;
  const { t } = useTranslate();

  /**
   * 校验文件是否匹配 accept
   */
  const validateAccept = useCallback(
    (file: File): string | null => {
      if (!accept) return null;

      const acceptTypes = accept.split(',').map((type) => type.trim().toLowerCase());
      const fileName = file.name.toLowerCase();
      const fileType = file.type.toLowerCase();

      const isMatch = acceptTypes.some((acceptType) => {
        if (acceptType.startsWith('.')) {
          // 扩展名匹配（如 .jpg, .png）
          return fileName.endsWith(acceptType);
        } else if (acceptType.includes('*')) {
          // MIME 类型通配符匹配（如 image/*）
          const [mainType] = acceptType.split('/');
          return fileType.startsWith(mainType + '/');
        } else {
          // 精确 MIME 类型匹配（如 image/jpeg）
          return fileType === acceptType;
        }
      });

      if (!isMatch) {
        return t('rejectedNames-accept', { rejectedNames: file.name, accept });
      }

      return null;
    },
    [accept, t],
  );

  /**
   * 校验单个规则
   */
  const validateRule = useCallback(
    async (file: File, rule: FileUploadRule): Promise<string | null> => {
      // 0. 检查文件类型是否匹配该规则
      if (!isFileTypeMatch(file, rule.fileTypes)) {
        // 文件类型不匹配，跳过该规则
        return null;
      }

      // 1. 自定义校验
      const customError = await validateCustomRule(file, rule, fileList);
      if (customError) {
        return customError;
      }

      // 2. 校验文件大小
      if (rule.maxSize !== undefined || rule.minSize !== undefined) {
        const sizeError = validateFileSize(file, rule.minSize, rule.maxSize);
        if (sizeError) {
          return rule.message || sizeError;
        }
      }

      // 3. 校验媒体时长
      const durationError = await validateMediaDuration(file, rule);
      if (durationError) {
        return durationError;
      }

      // 4. 校验图片分辨率
      const resolutionError = await validateImageResolution(file, rule);
      if (resolutionError) {
        return resolutionError;
      }

      // 5. 校验图片宽高比
      const aspectRatioError = await validateImageAspectRatio(file, rule);
      if (aspectRatioError) {
        return aspectRatioError;
      }

      return null;
    },
    [fileList],
  );

  /**
   * 校验文件
   * @param file - 待校验的文件
   * @returns ValidationResult - 校验结果
   */
  const validate = useCallback(
    async (file: File): Promise<ValidationResult> => {
      const errors: string[] = [];

      // 0. 优先校验 accept（最基础的校验）
      const acceptError = validateAccept(file);
      if (acceptError) {
        errors.push(acceptError);
        // accept 校验失败就直接返回，不需要继续其他校验
        return {
          valid: false,
          errors,
        };
      }

      // 1. 依次执行所有规则校验
      for (const rule of rules) {
        const error = await validateRule(file, rule);
        if (error) {
          errors.push(error);
        }
      }

      return {
        valid: errors.length === 0,
        errors,
      };
    },
    [rules, validateRule, validateAccept],
  );

  return {
    validate,
  };
};

// 拖拽文件时的校验 Hook
export const useDrop = (props: FileUploadProps) => {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (props.multiple === false) return;
      const droppedFiles = Array.from(e.dataTransfer.files);
      const { accept, onValidationFailed } = props;

      if (!accept || droppedFiles.length === 0) return;

      // 解析 accept 属性（支持 .jpg,.png 或 image/* 格式）
      const acceptTypes = accept.split(',').map((type) => type.trim().toLowerCase());

      // 检查每个文件是否匹配 accept
      const rejectedFiles = droppedFiles.filter((file) => {
        const fileName = file.name.toLowerCase();
        const fileType = file.type.toLowerCase();

        const isMatch = acceptTypes.some((acceptType) => {
          if (acceptType.startsWith('.')) {
            // 扩展名匹配（如 .jpg, .png）
            return fileName.endsWith(acceptType);
          } else if (acceptType.includes('*')) {
            // MIME 类型通配符匹配（如 image/*）
            const [mainType] = acceptType.split('/');
            return fileType.startsWith(mainType + '/');
          } else {
            // 精确 MIME 类型匹配（如 image/jpeg）
            return fileType === acceptType;
          }
        });

        return !isMatch; // 返回不匹配的文件
      });

      // 如果有不匹配的文件，给出提示
      if (rejectedFiles.length > 0) {
        const rejectedNames = rejectedFiles.map((f) => f.name).join(', ');
        const errorMsg = t('rejectedNames-accept', { rejectedNames, accept });
        onValidationFailed?.(errorMsg);
      }
    },
    [props.accept, props.multiple],
  );

  return {
    handleDrop,
  };
};
